
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import App from "@/pages/App";
import Settings from "@/pages/Settings";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import ProtectedRoute from "@/components/ProtectedRoute";
import Microsoft365Callback from "@/components/email/Microsoft365Callback";
import "./App.css";

const queryClient = new QueryClient();

// Make queryClient globally available for the Microsoft callback
declare global {
  interface Window {
    queryClient: typeof queryClient;
  }
}

function AppRouter() {
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Add queryClient to window for the Microsoft callback
    window.queryClient = queryClient;
    
    const fetchGoogleCredentials = async () => {
      try {
        console.log('Fetching Google credentials...');
        const { data, error } = await supabase.functions.invoke('get-google-credentials', {
          body: { // Add request body even if empty
            method: 'GET'
          }
        });
        
        if (error) {
          console.error('Error fetching Google credentials:', error);
          throw error;
        }
        
        if (data?.clientId) {
          console.log('Successfully fetched Google Client ID');
          setGoogleClientId(data.clientId);
        } else {
          console.error('No client ID returned from server');
          throw new Error('No client ID returned from server');
        }
      } catch (error) {
        console.error('Error fetching Google credentials:', error);
        toast.error('Failed to load Google authentication. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGoogleCredentials();
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!googleClientId) {
    return <div>Error loading authentication. Please refresh the page.</div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={googleClientId}>
        <TooltipProvider>
          <Router>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/auth/microsoft-callback" element={<Microsoft365Callback />} />
              <Route
                path="/app"
                element={
                  <ProtectedRoute>
                    <App />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Toaster />
          </Router>
        </TooltipProvider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
}

export default AppRouter;
